import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';

import { ApiUrl } from '../../../shared/models/apiurl.models';

/**
 * SCRUM-93 — "עוקב אחרי": ads from everyone you follow.
 *
 * The endpoint returns the same shape as the main listings, so the cards here
 * mirror the seller page's grid rather than introducing a third ad-card style.
 */
@Component({
  selector: 'app-feed',
  templateUrl: './feed.component.html',
  styleUrls: ['./feed.component.scss'],
  standalone: false,
})
export class FeedComponent implements OnInit {
  ads: any[] = [];

  selectedCategory = 'all';
  selectedSubcategory = 'all';
  categoryOptions: Array<{ id: string; name: string }> = [];
  subcategoryOptions: Array<{ id: string; name: string }> = [];
  filteredAds: any[] = [];

  loading = true;
  loadingMore = false;
  failed = false;
  hasNext = false;

  private page = 1;

  constructor(private http: HttpClient, private apiUrl: ApiUrl) {}

  ngOnInit(): void {
    this.load(1, true);
  }

  private get authOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: this.apiUrl.getJWTToken(),
      }),
    };
  }

  load(page: number, replace: boolean): void {
    if (replace) {
      this.loading = true;
      this.failed = false;
    } else {
      this.loadingMore = true;
    }

    this.http
      .get<any>(`${this.apiUrl.getFollowFeedUrl()}?page=${page}`, this.authOptions)
      .subscribe({
        next: (res) => {
          const results = res?.results ?? [];
          this.ads = replace ? results : this.ads.concat(results);
          this.rebuildFilterOptions();
          this.applyFilters();
          this.hasNext = !!res?.next;
          this.page = page;
          this.loading = false;
          this.loadingMore = false;
        },
        error: () => {
          this.loading = false;
          this.loadingMore = false;
          // A failed extra page must not destroy what is already on screen.
          if (replace) this.failed = true;
          else this.hasNext = false;
        },
      });
  }

  loadMore(): void {
    if (!this.hasNext || this.loadingMore) return;
    this.load(this.page + 1, false);
  }

  retry(): void {
    this.load(1, true);
  }

  adImage(ad: any): string | null {
    const images = ad?.images;
    if (Array.isArray(images) && images.length) return images[0]?.image ?? null;
    return null;
  }

  adPrice(ad: any): string | null {
    const p = ad?.price;
    if (p === null || p === undefined || p === '') return null;
    const n = Number(p);
    if (isNaN(n)) return null;
    return '₪' + n.toLocaleString('he-IL');
  }

  private rebuildFilterOptions(): void {
    const options = new Map<string, string>();
    this.ads.forEach((ad) => {
      const category = ad?.mainCategory ?? ad?.sconderyCategory?.mainCategoryKey;
      if (category?.id != null) options.set(String(category.id), category.name || 'קטגוריה');
    });
    this.categoryOptions = Array.from(options, ([id, name]) => ({ id, name }));
    this.rebuildSubcategoryOptions();
  }

  private rebuildSubcategoryOptions(): void {
    const options = new Map<string, string>();
    this.ads.forEach((ad) => {
      if (this.selectedCategory !== 'all' && this.categoryId(ad) !== this.selectedCategory) return;
      const category = ad?.sconderyCategory;
      if (category?.id != null) options.set(String(category.id), category.name || 'תת-קטגוריה');
    });
    this.subcategoryOptions = Array.from(options, ([id, name]) => ({ id, name }));
  }

  applyFilters(): void {
    this.filteredAds = this.ads.filter((ad) => {
      const categoryMatches = this.selectedCategory === 'all' || this.categoryId(ad) === this.selectedCategory;
      const subcategoryMatches = this.selectedSubcategory === 'all' || String(ad?.sconderyCategory?.id) === this.selectedSubcategory;
      return categoryMatches && subcategoryMatches;
    });
  }

  onCategoryChange(): void {
    this.selectedSubcategory = 'all';
    this.rebuildSubcategoryOptions();
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedCategory = 'all';
    this.selectedSubcategory = 'all';
    this.rebuildSubcategoryOptions();
    this.applyFilters();
  }

  private categoryId(ad: any): string {
    return String(ad?.mainCategory?.id ?? ad?.sconderyCategory?.mainCategoryKey?.id ?? '');
  }

  sellerName(ad: any): string {
    const user = ad?.User;
    return user?.store?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'מוכר ב-Sale4All';
  }

  sellerAvatar(ad: any): string | null {
    return ad?.User?.store?.logo || ad?.User?.avatar || ad?.User?.profilePhoto || null;
  }

  sellerInitials(ad: any): string {
    const name = this.sellerName(ad).trim();
    const parts = name.split(/\s+/).filter(Boolean);
    return (parts[0]?.charAt(0) || '') + (parts[1]?.charAt(0) || '');
  }

  categoryName(ad: any): string {
    return ad?.sconderyCategory?.name || ad?.mainCategory?.name || '';
  }

  cityName(ad: any): string {
    return ad?.city?.nameHe || ad?.City?.nameHe || '';
  }

  trackAd(index: number, ad: any): string | number {
    return ad?.id ?? index;
  }

  /** The store behind the ad, when the seller has one. */
  adStore(ad: any): any | null {
    return ad?.User?.store ?? null;
  }

  sellerId(ad: any): number | null {
    const id = ad?.User?.id;
    return typeof id === 'number' ? id : null;
  }
}
